import {
  CARD_COLOUR_LABELS,
  CARD_COLOUR_HEX,
  CARD_COLOURS,
  CARD_PRIORITIES,
  CARD_PRIORITY_LABELS,
  MOSCOW_LABELS,
  MOSCOW_LEVELS,
  cardDraftSchema,
  createCard,
  deleteCard,
  EMPTY_CARD_DRAFT,
  emptyStructuredContext,
  INTENT_LIMITS,
  intentCharacterCount,
  LIMITS,
  parentContextLayers,
  relatedComponents,
  updateCard,
  type Card,
  type CardDraft,
  type CardIntent,
} from '@blueprint/domain'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { TabPanel, Tabs } from '../../components/Tabs.js'
import { Button, ColourSwatches, Field, Select, TextArea, TextInput } from '../../components/controls.js'
import { IconPicker, StatusSelect, TagInput, TypeSelect, useProjectLabels } from '../../components/ItemFields.js'
import { loadCardVersion, loadCardVersions, useBoardStore } from '../../state/board-store.js'
import type { CardVersion } from '../../data/index.js'
import { cardLocation } from '../../state/selectors.js'
import { useUiStore } from '../../state/ui-store.js'
import { PromptPreviewDialog } from './PromptPreviewDialog.js'
import { AcceptanceCriteriaEditor } from './intent/AcceptanceCriteriaEditor.js'
import { Disclosure } from './intent/Disclosure.js'
import { ReferencesEditor } from './intent/ReferencesEditor.js'
import { RequirementEntriesEditor } from './intent/RequirementEntriesEditor.js'
import { ContextEditor, RelationshipsEditor } from '../context/ContextEditor.js'
import { FindingItem } from '../findings/Findings.js'
import { CardSourceLink } from '../mind-map/SourceLinks.js'
import styles from './CardEditorDialog.module.css'

type TabId = 'details' | 'context' | 'architecture'

function tagsToText(tags: readonly string[]): string {
  return tags.join(', ')
}

function textToTags(text: string): string[] {
  return text
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0)
}

/** Where a validation error lives, so the editor can take the user to it. */
function tabForError(key: string): TabId {
  if (key.startsWith('context.') || key.startsWith('relationships.')) return 'architecture'
  if (key.startsWith('intent.acceptanceCriteria') || key === 'intent.userOutcome') return 'details'
  return key.startsWith('intent.') ? 'context' : 'details'
}

/** The editable fields of a stored card or card version. */
function draftOf(card: Card): CardDraft {
  return {
    title: card.title,
    description: card.description,
    status: card.status,
    priority: card.priority,
    moscow: card.moscow,
    owner: card.owner,
    type: card.type,
    icon: card.icon,
    tags: card.tags,
    colour: card.colour,
    estimateMinutes: card.estimateMinutes,
    dueDate: card.dueDate,
    release: card.release,
    intent: card.intent,
    // Explicit empties, so restoring a version from before these existed clears them.
    context: card.context ?? emptyStructuredContext(),
    relationships: card.relationships ?? [],
  }
}

/**
 * The draft lives entirely in this dialog. Board state is untouched until Save, so
 * typing here cannot rerender a single card on the map. Cancel and Escape discard.
 */
export function CardEditorDialog() {
  const target = useUiStore((state) => state.editor)
  const closeEditor = useUiStore((state) => state.closeEditor)
  const openMove = useUiStore((state) => state.openMove)
  const requestConfirm = useUiStore((state) => state.requestConfirm)
  const card = useBoardStore((state) => (target?.mode === 'edit' ? state.entities?.cards[target.cardId] : undefined))
  const entities = useBoardStore((state) => state.entities)
  const activeBoardId = useBoardStore((state) => state.activeBoardId)

  const [draft, setDraft] = useState<CardDraft>(EMPTY_CARD_DRAFT)
  const [tagText, setTagText] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [tab, setTab] = useState<TabId>('details')
  const [previewOpen, setPreviewOpen] = useState(false)
  /** Saved versions, newest first; null while unknown or when history is unavailable. */
  const [versions, setVersions] = useState<CardVersion[] | null>(null)
  /** An earlier version shown read-only in place of the draft. */
  const [viewing, setViewing] = useState<{ version: number; card: Card } | null>(null)
  const [versionError, setVersionError] = useState('')
  /** The unsaved draft, put aside while an earlier version is on screen. */
  const stashed = useRef<{ draft: CardDraft; tagText: string } | null>(null)

  const open = target !== null
  const isEdit = target?.mode === 'edit'
  // Read afresh each time the editor opens, so tags added since are offered.
  const projectLabels = useProjectLabels(open ? activeBoardId : null)

  useEffect(() => {
    if (!open) return
    const source: CardDraft = card
      ? {
          title: card.title,
          description: card.description,
          status: card.status,
          priority: card.priority,
          moscow: card.moscow,
          owner: card.owner,
          type: card.type,
          icon: card.icon,
          tags: card.tags,
          colour: card.colour,
          estimateMinutes: card.estimateMinutes,
          dueDate: card.dueDate,
          release: card.release,
          intent: card.intent,
          context: card.context,
          relationships: card.relationships,
        }
      : EMPTY_CARD_DRAFT
    setDraft(source)
    setTagText(tagsToText(source.tags))
    setErrors({})
    // Always open on Details.
    setTab('details')
    setPreviewOpen(false)
    setViewing(null)
    setVersionError('')
    stashed.current = null
    // Re-seed only when the dialog target changes, never on every keystroke.
  }, [open, target?.mode, isEdit ? target.cardId : null, card?.id])

  const editedCardId = open && isEdit ? target.cardId : null
  useEffect(() => {
    setVersions(null)
    if (!editedCardId) return
    let cancelled = false
    loadCardVersions(editedCardId)
      .then((list) => {
        if (!cancelled) setVersions(list)
      })
      .catch(() => {
        if (!cancelled) setVersionError('Earlier versions could not be loaded.')
      })
    return () => {
      cancelled = true
    }
    // A new version arriving from elsewhere refreshes the list.
  }, [editedCardId, card?.version])

  const location = useMemo(() => {
    if (!entities || !target) return undefined
    if (target.mode === 'create') return cardLocation(entities, { columnId: target.columnId, rowId: target.rowId })
    return card ? cardLocation(entities, card) : undefined
  }, [entities, target, card])

  // The scopes this card inherits from. A new card inherits from the cell it is created in.
  const contextParents = useMemo(() => {
    if (!entities || !target) return []
    const cell = target.mode === 'create' ? target : card
    return cell ? parentContextLayers(entities, { scope: 'card', rowId: cell.rowId, columnId: cell.columnId }) : []
  }, [entities, target, card])

  const cardId = target?.mode === 'edit' ? target.cardId : null
  const cardFindings = useMemo(
    () =>
      cardId && entities
        ? Object.values(entities.findings ?? {})
            .filter((finding) => finding.targetId === cardId && finding.status === 'open')
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        : [],
    [entities, cardId],
  )
  const related = useMemo(
    () => (entities ? relatedComponents(entities, cardId, draft.relationships ?? []) : []),
    [entities, cardId, draft.relationships],
  )

  // Every hook must run before the early return below.
  const candidate: CardDraft = useMemo(
    () => ({ ...draft, tags: textToTags(tagText) }),
    [draft, tagText],
  )

  /** Whether the draft differs from the saved card, for the preview's unsaved notice. */
  const dirty = useMemo(() => {
    if (!card) return true
    const saved: CardDraft = {
      title: card.title,
      description: card.description,
      status: card.status,
      priority: card.priority,
      moscow: card.moscow,
      owner: card.owner,
      type: card.type,
      icon: card.icon,
      tags: card.tags,
      colour: card.colour,
      estimateMinutes: card.estimateMinutes,
      dueDate: card.dueDate,
      release: card.release,
      intent: card.intent,
      context: card.context,
      relationships: card.relationships,
    }
    return JSON.stringify(candidate) !== JSON.stringify(saved)
  }, [candidate, card])

  if (!target) return null

  const set = <K extends keyof CardDraft>(key: K, value: CardDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  const setIntent = (updater: (intent: CardIntent) => CardIntent) =>
    setDraft((current) => ({ ...current, intent: updater(current.intent) }))

  const submit = () => {
    const parsed = cardDraftSchema.safeParse(candidate)
    if (!parsed.success) {
      const next: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.') || 'form'
        next[key] ??= issue.message
      }
      setErrors(next)
      const firstKey = Object.keys(next)[0]
      if (firstKey) setTab(tabForError(firstKey))
      return
    }

    const total = intentCharacterCount(parsed.data.intent)
    if (total > INTENT_LIMITS.aggregate) {
      setErrors({
        form: `Card context is ${total.toLocaleString()} characters, over the ${INTENT_LIMITS.aggregate.toLocaleString()} limit. Shorten or remove some entries.`,
      })
      setTab('context')
      return
    }

    // Dismiss first, then commit on the next frame. Tearing down the native modal's top
    // layer and rerendering the board in one frame measured far slower on a large board
    // than doing them in sequence, and the dialog closing immediately is what a user feels.
    closeEditor()
    const boardId = useBoardStore.getState().activeBoardId
    const commit = () => {
      const store = useBoardStore.getState()
      // The board may have been switched in the meantime; never apply to the wrong one.
      if (store.activeBoardId !== boardId) return
      if (target.mode === 'create') {
        store.run((state) =>
          createCard(state, { rowId: target.rowId, columnId: target.columnId, draft: parsed.data }),
        )
      } else {
        store.run((state) => updateCard(state, target.cardId, parsed.data))
      }
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(commit)
    else commit()
  }

  const remove = () => {
    if (target.mode !== 'edit') return
    const title = card?.title ?? 'this card'
    closeEditor()
    requestConfirm({
      title: 'Delete card',
      message: `"${title}" will be removed from the board.`,
      confirmLabel: 'Delete',
      undoable: true,
      onConfirm: () => useBoardStore.getState().run((state) => deleteCard(state, target.cardId)),
    })
  }

  const intent = draft.intent
  const contextCount =
    intent.requirements.length +
    intent.references.length +
    [intent.inScope, intent.outOfScope, intent.constraints, intent.notes, intent.verification, intent.openQuestions]
      .filter((value) => value.trim().length > 0).length

  const localContext = draft.context ?? emptyStructuredContext()
  const architectureCount =
    (draft.relationships?.length ?? 0) +
    Object.entries(localContext).filter(([key, value]) =>
      key === 'version' ? false : typeof value === 'string' ? value.length > 0 : Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0,
    ).length

  const errorTabs = new Set(Object.keys(errors).map(tabForError))

  const currentVersion = card?.version ?? 1
  const selectVersion = async (value: string) => {
    if (target.mode !== 'edit') return
    setVersionError('')
    if (value === 'current') {
      if (stashed.current) {
        setDraft(stashed.current.draft)
        setTagText(stashed.current.tagText)
      }
      stashed.current = null
      setViewing(null)
      return
    }
    const version = Number(value)
    try {
      const snapshot = await loadCardVersion(target.cardId, version)
      stashed.current ??= { draft, tagText }
      const shown = draftOf(snapshot)
      setDraft(shown)
      setTagText(tagsToText(shown.tags))
      setErrors({})
      setViewing({ version, card: snapshot })
    } catch {
      setVersionError(`Version ${version} could not be loaded.`)
    }
  }

  /** Saving an earlier version's content makes it the next version; history is kept. */
  const restoreVersion = () => {
    if (target.mode !== 'edit' || !viewing) return
    const restored = draftOf(viewing.card)
    closeEditor()
    useBoardStore.getState().run((state) => updateCard(state, target.cardId, restored))
  }

  const versionPicker =
    target.mode === 'edit' ? (
      <label className={styles.versionPicker}>
        <span>Version</span>
        <Select
          aria-label="Card version"
          value={viewing ? String(viewing.version) : 'current'}
          disabled={!versions || versions.length <= 1}
          onChange={(event) => void selectVersion(event.target.value)}
        >
          <option value="current">v{currentVersion} (current)</option>
          {(versions ?? [])
            .filter((entry) => entry.version !== currentVersion)
            .map((entry) => (
              <option key={entry.version} value={entry.version}>
                v{entry.version} · {new Date(entry.createdAt).toLocaleString()}
              </option>
            ))}
        </Select>
      </label>
    ) : null

  return (
    <>
      <Dialog
        open={open}
        xwide
        title={target.mode === 'create' ? 'Add card' : 'Edit card'}
        onClose={closeEditor}
        onSubmit={viewing ? undefined : submit}
        headerActions={versionPicker}
        toolbar={
          <Tabs
            idPrefix="card-editor"
            label="Card sections"
            active={tab}
            onChange={(id) => setTab(id as TabId)}
            tabs={[
              { id: 'details', label: 'Details', invalid: errorTabs.has('details') },
              { id: 'context', label: 'Context', count: contextCount, invalid: errorTabs.has('context') },
              {
                id: 'architecture',
                label: 'Architecture',
                count: architectureCount,
                invalid: errorTabs.has('architecture'),
              },
            ]}
          />
        }
        footer={
          viewing ? (
            <>
              <div className={styles.footerLeft}>
                <Button onClick={() => setPreviewOpen(true)}>Prompt Preview</Button>
              </div>
              <Button onClick={() => void selectVersion('current')}>Back to current</Button>
              <Button variant="primary" onClick={restoreVersion}>
                Restore this version
              </Button>
            </>
          ) : (
          <>
            <div className={styles.footerLeft}>
              <Button onClick={() => setPreviewOpen(true)}>Prompt Preview</Button>
              {target.mode === 'edit' ? (
                <>
                  <Button
                    onClick={() => {
                      closeEditor()
                      openMove(target.cardId)
                    }}
                  >
                    Move…
                  </Button>
                  <Button variant="danger" onClick={remove}>
                    Delete
                  </Button>
                  <CardSourceLink nodeId={card?.sourceNodeId} cardTitle={card?.title ?? ''} />
                </>
              ) : null}
            </div>
            <Button onClick={closeEditor}>Cancel</Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </>
          )
        }
      >
        {viewing ? (
          <p role="status" className={styles.versionBanner}>
            Viewing version {viewing.version} of {currentVersion}, read-only. Restoring it saves its content as version{' '}
            {currentVersion + 1}; no version is removed.
          </p>
        ) : null}
        {versionError ? (
          <p role="alert" style={{ color: 'var(--danger)', marginTop: 0 }}>
            {versionError}
          </p>
        ) : null}
        {errors['form'] ? (
          <p role="alert" style={{ color: 'var(--danger)', marginTop: 0 }}>
            {errors['form']}
          </p>
        ) : null}

        {/* A disabled fieldset makes an earlier version read-only without touching each control. */}
        <fieldset disabled={viewing !== null} className={styles.versionFieldset}>
        <TabPanel idPrefix="card-editor" tabId="details" active={tab}>
          <div className={styles.stack}>
            <Field label="Title" error={errors['title'] ?? null}>
              {(id) => (
                <TextInput
                  id={id}
                  data-autofocus
                  value={draft.title}
                  maxLength={LIMITS.cardTitle}
                  onChange={(event) => set('title', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Description"
              error={errors['description'] ?? null}
              hint={`The general explanation of the work. ${draft.description.length.toLocaleString()} of ${LIMITS.description.toLocaleString()} characters.`}
            >
              {(id) => (
                <TextArea
                  id={id}
                  value={draft.description}
                  maxLength={LIMITS.description}
                  onChange={(event) => set('description', event.target.value)}
                />
              )}
            </Field>

            <section className={styles.planning}>
              <p className={styles.planningTitle}>Planning</p>
              <div className={styles.planningGrid}>
                <Field label="Type">{(id) => <TypeSelect id={id} value={draft.type} onChange={(type) => set('type', type)} />}</Field>
                <Field label="Status">
                  {(id) => <StatusSelect id={id} value={draft.status} onChange={(status) => status && set('status', status)} />}
                </Field>
                <Field label="Priority">
                  {(id) => (
                    <Select
                      id={id}
                      value={draft.priority ?? ''}
                      onChange={(event) =>
                        set('priority', (event.target.value || null) as CardDraft['priority'])
                      }
                    >
                      <option value="">Not set</option>
                      {CARD_PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {CARD_PRIORITY_LABELS[priority]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="MoSCoW">
                  {(id) => (
                    <Select
                      id={id}
                      value={draft.moscow ?? ''}
                      onChange={(event) =>
                        set('moscow', (event.target.value || null) as CardDraft['moscow'])
                      }
                    >
                      <option value="">Not set</option>
                      {MOSCOW_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {MOSCOW_LABELS[level]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Colour">
                  {(id) => (
                    <ColourSwatches
                      id={id}
                      label="Colour"
                      options={CARD_COLOURS.map((colour) => ({
                        value: colour,
                        label: CARD_COLOUR_LABELS[colour],
                        colour: colour === 'default' ? null : CARD_COLOUR_HEX[colour],
                      }))}
                      value={draft.colour}
                      onPick={(colour) => set('colour', colour as CardDraft['colour'])}
                    />
                  )}
                </Field>
                <Field label="Owner" error={errors['owner'] ?? null}>
                  {(id) => (
                    <TextInput
                      id={id}
                      value={draft.owner}
                      maxLength={LIMITS.owner}
                      onChange={(event) => set('owner', event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Release" error={errors['release'] ?? null}>
                  {(id) => (
                    <TextInput
                      id={id}
                      value={draft.release}
                      maxLength={LIMITS.release}
                      onChange={(event) => set('release', event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Estimate (minutes)" error={errors['estimateMinutes'] ?? null}>
                  {(id) => (
                    <TextInput
                      id={id}
                      type="number"
                      min={0}
                      step={5}
                      value={draft.estimateMinutes ?? ''}
                      onChange={(event) =>
                        set('estimateMinutes', event.target.value === '' ? null : Number(event.target.value))
                      }
                    />
                  )}
                </Field>
                <Field label="Due date" error={errors['dueDate'] ?? null}>
                  {(id) => (
                    <TextInput
                      id={id}
                      type="date"
                      value={draft.dueDate ?? ''}
                      onChange={(event) => set('dueDate', event.target.value === '' ? null : event.target.value)}
                    />
                  )}
                </Field>
                <div className={styles.full}>
                  <Field
                    label="Tags"
                    error={errors['tags'] ?? null}
                    hint={`Press Enter to add a tag, new or from the list. Up to ${LIMITS.tagCount} tags of ${LIMITS.tag} characters.`}
                  >
                    {(id) => <TagInput id={id} value={textToTags(tagText)} suggestions={projectLabels.tags} onChange={(tags) => setTagText(tagsToText(tags))} />}
                  </Field>
                </div>
                <div className={styles.full}>
                  <Field label="Icon">{() => <IconPicker value={draft.icon} onPick={(icon) => set('icon', icon)} />}</Field>
                </div>
              </div>
              {location ? (
                <p className={styles.location}>
                  On <strong>{location.boardName}</strong> — column{' '}
                  <strong>{location.columnGroup ? `${location.columnGroup} / ${location.column}` : location.column}</strong>
                  , row <strong>{`${location.swimlane} / ${location.row}`}</strong>
                </p>
              ) : null}
            </section>

            <Field
              label="User outcome"
              error={errors['intent.userOutcome'] ?? null}
              hint="Who benefits and why. Optional."
            >
              {(id) => (
                <TextArea
                  id={id}
                  value={intent.userOutcome}
                  maxLength={INTENT_LIMITS.userOutcome}
                  placeholder="A returning customer can resume an unfinished order."
                  style={{ minHeight: 70 }}
                  onChange={(event) => setIntent((current) => ({ ...current, userOutcome: event.target.value }))}
                />
              )}
            </Field>

            <div>
              <p className={styles.sectionTitle}>Acceptance criteria</p>
              <p className={styles.sectionHint}>
                Observable results that establish success. Verification notes, in Context, describe how to check them.
              </p>
              <div style={{ height: 8 }} />
              <AcceptanceCriteriaEditor
                criteria={intent.acceptanceCriteria}
                onChange={(acceptanceCriteria) => setIntent((current) => ({ ...current, acceptanceCriteria }))}
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel idPrefix="card-editor" tabId="context" active={tab}>
          <div className={styles.stack}>
            <p className={styles.sectionHint}>
              Everything here is optional and written by you. Blueprint organises it into a prompt; it does not
              generate requirements. Collapsing a section never removes its content or leaves it out of a prompt.
            </p>

            <LongField
              label="In scope"
              value={intent.inScope}
              limit={INTENT_LIMITS.inScope}
              error={errors['intent.inScope']}
              placeholder="Behaviour the implementation should cover"
              onChange={(inScope) => setIntent((current) => ({ ...current, inScope }))}
            />
            <LongField
              label="Out of scope"
              value={intent.outOfScope}
              limit={INTENT_LIMITS.outOfScope}
              error={errors['intent.outOfScope']}
              placeholder="Explicit boundaries that prevent accidental expansion"
              onChange={(outOfScope) => setIntent((current) => ({ ...current, outOfScope }))}
            />
            <LongField
              label="Constraints"
              value={intent.constraints}
              limit={INTENT_LIMITS.constraints}
              error={errors['intent.constraints']}
              placeholder="Required compatibility, accessibility, performance or product rules"
              onChange={(constraints) => setIntent((current) => ({ ...current, constraints }))}
            />
            <LongField
              label="Technical and UX notes"
              value={intent.notes}
              limit={INTENT_LIMITS.notes}
              error={errors['intent.notes']}
              placeholder="Known interfaces, interaction expectations, relevant areas of the code"
              onChange={(notes) => setIntent((current) => ({ ...current, notes }))}
            />
            <LongField
              label="Verification notes"
              value={intent.verification}
              limit={INTENT_LIMITS.verification}
              error={errors['intent.verification']}
              placeholder="How a person or agent can check the result"
              onChange={(verification) => setIntent((current) => ({ ...current, verification }))}
            />
            <LongField
              label="Open questions"
              value={intent.openQuestions}
              limit={INTENT_LIMITS.openQuestions}
              error={errors['intent.openQuestions']}
              placeholder="Unresolved decisions the implementer should not silently invent. &quot;Unknown — needs investigation&quot; is a useful answer."
              onChange={(openQuestions) => setIntent((current) => ({ ...current, openQuestions }))}
            />

            <div>
              <p className={styles.sectionTitle}>Requirement groups</p>
              <p className={styles.sectionHint}>
                Add the groups relevant to this work, in any combination. Each offers questions to answer — they are
                suggestions, not answers or default requirements.
              </p>
              <div style={{ height: 10 }} />
              <RequirementEntriesEditor
                entries={intent.requirements}
                onChange={(requirements) => setIntent((current) => ({ ...current, requirements }))}
              />
            </div>

            <div>
              <p className={styles.sectionTitle}>References</p>
              <p className={styles.sectionHint}>
                Manually supplied card context. Not connected, verified or synchronised Teamcentric Context content.
              </p>
              <div style={{ height: 10 }} />
              <ReferencesEditor
                references={intent.references}
                onChange={(references) => setIntent((current) => ({ ...current, references }))}
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel idPrefix="card-editor" tabId="architecture" active={tab}>
          <div className={styles.stack}>
            {entities && cardId && cardFindings.length > 0 ? (
              <div>
                <p className={styles.sectionTitle}>Findings</p>
                <p className={styles.sectionHint}>
                  Observed in the implementation. Accepting a proposal saves it to this card as a new version.
                </p>
                <div style={{ height: 8 }} />
                {cardFindings.map((finding) => (
                  <FindingItem
                    key={finding.id}
                    finding={finding}
                    entities={entities}
                    showTarget={false}
                    blockedReason={dirty || viewing ? 'Save or cancel your edits first.' : undefined}
                    onDecided={() => {
                      // The card itself changed; take its new context and relationships into the draft.
                      const latest = useBoardStore.getState().entities?.cards[cardId]
                      if (latest) {
                        setDraft((current) => ({ ...current, context: latest.context, relationships: latest.relationships }))
                      }
                    }}
                  />
                ))}
              </div>
            ) : null}
            <p className={styles.sectionHint}>
              Software context for this card. Values from the board, swimlane, row and column are inherited and shown
              beside each field; anything written here overrides or adds to them for this card only.
            </p>
            <ContextEditor
              value={localContext}
              onChange={(context) => set('context', context)}
              parents={contextParents}
              errors={errors}
              onOpenSource={(source) => {
                if (source.scope === 'board') useUiStore.getState().setContextOwner({ scope: 'board' })
                else if (source.scope !== 'card') useUiStore.getState().setContextOwner({ scope: source.scope, id: source.id })
              }}
            />
            {entities ? (
              <div>
                <p className={styles.sectionTitle}>Relationships</p>
                <p className={styles.sectionHint}>Typed architectural links from this card to other cards on the board.</p>
                <div style={{ height: 10 }} />
                <RelationshipsEditor
                  relationships={draft.relationships ?? []}
                  onChange={(relationships) => set('relationships', relationships)}
                  entities={entities}
                  cardId={cardId}
                />
                {Object.entries(errors)
                  .filter(([key]) => key.startsWith('relationships.'))
                  .map(([key, message]) => (
                    <p key={key} role="alert" style={{ color: 'var(--danger)', fontSize: 12 }}>
                      {message}
                    </p>
                  ))}
              </div>
            ) : null}
          </div>
        </TabPanel>
        </fieldset>
      </Dialog>

      <PromptPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        draft={candidate}
        location={location}
        cardId={target.mode === 'edit' ? target.cardId : null}
        unsaved={dirty}
        onIntentChange={(next) => setDraft((current) => ({ ...current, intent: next }))}
        contextParents={contextParents}
        related={related}
      />
    </>
  )
}

interface LongFieldProps {
  label: string
  value: string
  limit: number
  error?: string | undefined
  placeholder: string
  onChange: (value: string) => void
}

/** Optional long-form field, collapsed until it is needed or already has content. */
function LongField({ label, value, limit, error, placeholder, onChange }: LongFieldProps) {
  const filled = value.trim().length > 0
  return (
    <Disclosure
      label={label}
      defaultOpen={filled || Boolean(error)}
      filled={filled ? `${value.length.toLocaleString()} characters` : undefined}
    >
      {/* The summary already names this field; labelling it twice would be noise. */}
      <TextArea
        aria-label={label}
        value={value}
        maxLength={limit}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span role="alert" style={{ color: 'var(--danger)', fontSize: 12 }}>
          {error}
        </span>
      ) : null}
    </Disclosure>
  )
}
